<template>
  <div>
    <h2>Projekte</h2>
    <p>
      Willkommen zu den Projektdetails. Bei der Neuerstellung eines Projekts werden hier die
      Projektinformationen eingegeben. Um diese abzuspeichern muss mindestens die Projektbezeichnung
      eingegeben sein. Außerdem müssen die historischen Energieverbrauchsdaten für das Einsparpotential eingetragen sein.
      Über das Feld Bemerkung können Sie einen freien Hinweis zum Projekt hinterlegen. Zusätzlich kann
      das Projekt einer Branche sowie – nach Auswahl der Branche – einer Kategorie zugewiesen werden und
      hinterlegen Sie die Bruttogrundfläche des zu betrachtenden Gebäudes. Die Projektdaten beziehen sich
      auf den Projektstandort, die Kundendaten auf den Auftraggeber.
    </p>
    <p>Folgende Funktionen sind hier vorhanden:</p>
    <ul>
      <li>
        Allgemeine Kundendaten hinterlegen.
      </li>
      <li>
        Historische Energieverbrauchsdaten hinterlegen.
      </li>
      <li>
        Energiepreise für Berechnung anpassen.
      </li>
      <li>
        Primärenergiefaktoren für Berechnung anpassen.
      </li>
      <li>
        CO2-Äquivalente für Berechnung anpassen.
      </li>
      <li>
        Standardwerte für Energiepreise, Primärenergiefaktoren und CO2-Äquivalente übernehmen.
      </li>
      <li>
        Einspeisedaten (Einspeiseenergie und -vergütung) erfassen.
      </li>
      <li>
        Zusätzlich können der Standort und die Energiedaten visuell dargestellt werden.
      </li>
    </ul>
    <v-expansion-panels accordion class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Zusätzliche Informationen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <h3>Energieverbrauch und Kosten aus Energieabrechnungen</h3>
            <p>
              Hier ist vorgesehen, die Jahres-Energiekosten und -verbräuche gemäß den Rechnungen
              der Energielieferanten (Stadtwerke, Stromversorger, Heizölrechnung, etc.) anzugeben.
              Es sind mindestens Werte für ein vollständiges Jahr und alle verwendeten Energieträger anzugeben.
              Da Abrechnungszeiträume
              der Energieversorger nicht immer mit dem Kalenderjahr übereinstimmen, können näherungsweise
              auch die Daten eines abweichenden Abrechnungszeitraum verwendet werden, z.B. Anfang März bis
              Ende Februar. Um die Entwicklung des Verbrauchs und der Kosten beurteilen zu können, ist es
              sinnvoll, die Daten der letzten drei Jahre anzugeben.
            </p>
            <p>
              Über die Auswahl <strong>Jahr</strong> wechseln Sie zwischen den Jahren; es können Daten
              für bis zu zehn zurückliegende Jahre erfasst werden. Die eingegebenen Energieträger sind
              z.B. Netzstrom, Erdgas H, Öl, Pellets, Hackschnitzel und Fernwärme.
            </p>
              <dl>
                <dt><strong>Erdgas:</strong></dt>
                <dd>
                    Eingabe des in den Gasrechnungen angegebenen Erdgasverbrauchs. Dieser ist
                    üblicherweise auf den Brennwert (Hs, früher Ho) bezogen — die hinterlegten
                    CO₂-Faktoren sind auf denselben Bezug umgerechnet.
                </dd>
                <dt><strong>Erdgas H oder Erdgas L?</strong></dt>
                <dd>
                    <strong>Erdgas H</strong> ist die richtige Wahl, sofern die Rechnung nichts
                    anderes ausweist. Die Bezeichnungen stammen aus dem DVGW-Arbeitsblatt G 260;
                    in älteren EARNS-Projekten stehen dafür die Prüfgas-Kürzel nach DIN EN 437
                    („Gas E“ für H, „Gas LL“ für L). Für die Rechnung macht die Wahl in EARNS
                    praktisch keinen Unterschied: CO₂-Faktor, Primärenergiefaktor und
                    Vorgabepreis sind für beide gleich, und der einzige abweichende Wert wirkt
                    sich mit rund einem Achtelprozent auf das Ergebnis der Kesselberechnung aus.
                    <strong>Erdgas L</strong> wird deshalb nur noch in Projekten angeboten, die
                    es bereits verwenden — dort bleibt es unverändert rechenbar, damit
                    bestehende Berichte nachvollziehbar bleiben.
                </dd>
                <dt><strong>Heizöl:</strong></dt>
                <dd>
                    Eingabe des in den Heizöl-Rechnungen angegebenen Heizölverbrauchs. Dieser
                    ist auf den Brennwert (Hs, früher Ho) zu beziehen.
                    Die Umrechnung hierzu erfolgt mit folgendem Zusammenhang:
                </dd>
                <dt></dt>
                <dd><strong>
                  1 Liter Heizöl = 10,6 kWh (Hs)
                </strong></dd>
              </dl>
            <p>
              Zuletzt wird der Durchschnittspreis (€/kWh) aus den eingegebenen
              Jahres-Energiekosten (€/a) und dem Jahres-Energieverbrauch (MWh/a) angezeigt.
            </p>

          <h3>Energiepreise</h3>
            <p>
              Für Energiepreise wurden am 23.11.2022 Standardwerte erhoben.
            </p>
            <dl>
              <dt><strong>Hinweis:</strong></dt>
              <dd>
                Im Einsparpotential werden sie auf fehlende Energiepreise hingewiesen
              </dd>
            </dl>
          <h3>Primärenergiefaktoren</h3>
            <p>
              Die Primärenergiefaktoren richten sich nach der DIN 18599 „Energetische Bewertung
              von Gebäuden“ und der aktuellen Energieeinsparverordnung (EnEV 2014). Bei Bedarf
              können Sie diese Werte je nach Projekt anpassen.
            </p>

          <h3>CO2-Äquivalente</h3>
            <p>
              Die CO2-Äquivalente richten sich nach GEMIS 4.9 (Globales Emissions-Modell
              integrierter Systeme). Bei Bedarf können Sie diese Werte ebenfalls je nach Projekt anpassen.
              Die Aktuellsten C02-Äquivalente finden Sie im "Informationsblatt CO2-Faktoren" der bafa.
            </p>

          <h3>Standardwerte</h3>
            <p>
              Neben jedem Eingabefeld für Energiepreise, Primärenergiefaktoren und CO2-Äquivalente
              befindet sich die Schaltfläche „Standardwert nehmen“ (Symbol ↺). Damit setzen Sie den
              Wert des jeweiligen Energieträgers auf den hinterlegten Standardwert zurück, falls Sie
              ihn zuvor angepasst haben.
            </p>

          <h3>Einspeisedaten</h3>
            <p>
              Im Bereich Einspeisedaten erfassen Sie eingespeiste und vergütete Energie, z.B. aus
              einer Photovoltaik-Anlage, und legen fest, wie die Einspeisung bilanziert wird:
            </p>
            <dl>
              <dt><strong>CO₂-Gutschrift für Einspeisung:</strong></dt>
              <dd>
                Legt fest, ob ins Netz eingespeiste Energie als Verdrängung von Netzenergie in die
                CO₂- und Primärenergiebilanz eingeht. Standardmäßig ist die Option ausgeschaltet,
                denn eingespeiste Energie verlässt die Bilanzgrenze des Unternehmens und wird nur
                vergütet. Eingeschaltet wird die eingespeiste Menge mit dem Faktor der verdrängten
                Netzenergie gutgeschrieben — sinnvoll etwa für eine Treibhausgasbilanz nach Scope 2,
                die im Bericht dann als solche kenntlich gemacht werden sollte. Die Einstellung gilt
                für das <strong>gesamte Projekt</strong> und für alle eingespeisten Energieträger,
                damit einzelne Maßnahmen nicht unterschiedlich bilanziert werden. Auf die Vergütung
                in Euro hat sie keinen Einfluss.
              </dd>
              <dt><strong>Einspeiseenergie und -vergütung aus Energieabrechnungen:</strong></dt>
              <dd>
                Eingabe der pro Jahr eingespeisten Energiemengen und der zugehörigen Vergütung gemäß
                den Abrechnungen. Wie beim Energieverbrauch wählen Sie das Jahr über die Auswahl aus.
              </dd>
              <dt><strong>Einspeisevergütung:</strong></dt>
              <dd>
                Eingabe der Vergütung (€/kWh) je Energieträger für die eingespeiste Energie.
              </dd>
            </dl>

          <h3>Karte &amp; Statistik</h3>
            <p>
              Über die Schaltfläche „Auf der Karte anzeigen“ wird der eingegebene Standort (Straße,
              PLZ, Stadt) geokodiert und auf einer Karte dargestellt. Die Statistik-Karte zeigt
              zusätzlich Kennzahlen zu den eingegebenen Energiedaten des Projekts.
            </p>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
defineOptions({
  name: "ProjectDetailsHelp",
});
</script>

<style scoped></style>
